import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getShareDetails } from '@/services/shareVideoService';
import { ShareCharts } from '@/components/share-video/ShareCharts';
import { ShareEventsTable } from '@/components/share-video/ShareEventsTable';

export default function ShareVideoDetails() {
  useLanguage();
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['share-details', videoId],
    queryFn: () => getShareDetails(videoId!),
    enabled: !!videoId
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 md:px-8 lg:px-10 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 md:px-8 lg:px-10 py-8 text-center">
          <p className="text-destructive mb-4">{translateInterfaceText("Erro ao carregar detalhes")}</p>
          <Button onClick={() => navigate('/distribuir')}>{translateInterfaceText("Voltar")}</Button>
        </div>
      </Layout>
    );
  }

  const { video, shareLink, kpis, chartByDay, topCountries, topStates, events } = data;
  const isActive = shareLink?.status === 'active';

  return (
    <Layout>
      <div className="bg-background">
        <div className="max-w-5xl mx-auto px-6 md:px-8 lg:px-10 py-8">
          {/* Header Card */}
          <div className="bg-card border rounded-xl p-5 mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/distribuir')}
              className="mb-4 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {translateInterfaceText("Voltar")}</Button>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Thumbnail */}
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                <img
                  src={video.thumbUrl}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <h1 className="text-lg font-semibold text-foreground">{video.title}</h1>
                  {shareLink && (
                    <Badge className={cn(
                      isActive ? 'bg-[#2D6CF6] text-white' : 'bg-slate-500 text-white'
                    )}>
                      {isActive ? translateInterfaceText("Ativo") : translateInterfaceText("Desativado")}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {kpis.totalCopies} {" "}{translateInterfaceText("compartilhamentos")}</p>
              </div>
            </div>
          </div>

          {/* Charts - Always visible */}
          <ShareCharts
            chartByDay={chartByDay}
            topStates={topStates || topCountries?.map(c => ({ state: c.country, copies: c.copies })) || []}
          />

          {/* Events Table - Always visible */}
          <ShareEventsTable events={events} />
        </div>
      </div>
    </Layout>
  );
}
